import { Link } from "react-router";
import { motion } from "framer-motion";
import { ArrowLeft, Sparkles } from "lucide-react";
import { brandColors as colors, usePalette } from "@/lib/palette";
import { fonts } from "@/lib/fonts";
import { fadeUp, stagger } from "@/lib/animations";
import type { GuideCard as GuideCardType, GuideTopicCard } from "@/lib/guides";
import Footer from "@/components/ui/Footer";
import Blob from "@/components/ui/Blob";
import GuideCard from "@/components/guides/GuideCard";

type Props = {
  topic: GuideTopicCard;
  guides: GuideCardType[];
};

export default function GuideTopicPage({ topic, guides }: Props) {
  const theme = usePalette();
  const accent = topic.color ?? theme.accent;

  return (
    <div className="min-h-dvh" style={{ backgroundColor: colors.cream }}>

      <section
        className="relative overflow-hidden pt-32 pb-20"
        style={{ backgroundColor: colors.cream }}
      >
        <Blob className="w-96 h-96 -top-20 right-0" color={theme.blob2} />
        <Blob className="w-72 h-72 bottom-0 -left-10" color={theme.blob3} />

        <div className="relative z-10 mx-auto max-w-5xl px-6">
          <Link
            to="/guides"
            className="mb-6 inline-flex items-center gap-2 text-xs uppercase"
            style={{
              fontFamily: fonts.body,
              fontWeight: 700,
              letterSpacing: "0.16em",
              color: `${colors.navy}88`,
            }}
          >
            <ArrowLeft size={14} /> All guides
          </Link>

          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="mb-3 inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs uppercase"
            style={{
              fontFamily: fonts.body,
              fontWeight: 800,
              letterSpacing: "0.18em",
              color: accent,
              backgroundColor: `${accent}1a`,
            }}
          >
            <Sparkles size={12} /> {topic.guideCount} {topic.guideCount === 1 ? "guide" : "guides"}
          </motion.p>

          <motion.h1
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.05 }}
            className="text-5xl sm:text-6xl"
            style={{
              fontFamily: fonts.logo,
              fontWeight: 400,
              color: colors.navy,
              lineHeight: 1.1,
            }}
          >
            {topic.name}
          </motion.h1>
          {topic.description && (
            <motion.p
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.15 }}
              className="mt-5 max-w-2xl text-base"
              style={{
                fontFamily: fonts.body,
                fontWeight: 500,
                color: `${colors.navy}b3`,
                letterSpacing: "0.03em",
                lineHeight: 1.7,
              }}
            >
              {topic.description}
            </motion.p>
          )}
        </div>
      </section>

      <section className="pb-24" style={{ backgroundColor: colors.cream }}>
        <div className="mx-auto max-w-6xl px-6">
          {guides.length === 0 ? (
            <div
              className="rounded-3xl p-12 text-center"
              style={{
                backgroundColor: theme.sectionBg,
                border: `1px dashed ${colors.tan}55`,
                fontFamily: fonts.body,
                color: `${colors.navy}aa`,
              }}
            >
              No guides in this topic yet. Check back soon.
            </div>
          ) : (
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-60px" }}
              variants={stagger}
              className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3"
            >
              {guides.map((g, i) => (
                <motion.div key={g.id} variants={fadeUp} custom={i}>
                  <GuideCard guide={g} index={i} showTopic={false} />
                </motion.div>
              ))}
            </motion.div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
